import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FaFire, FaTag } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import {
    FaMapMarkerAlt,
    FaHeart,
    FaRegHeart,
    FaWifi,
    FaSnowflake,
    FaCoffee,
    FaSwimmingPool,
    FaUmbrellaBeach,
    FaSpa,
    FaDumbbell,
    FaParking,
    FaShuttleVan,
    FaUtensils,
    FaCocktail,
    FaConciergeBell,
    FaCheck,
    FaCalendarAlt
} from "react-icons/fa";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

export default function HotelDetail() {
    const [hotel, setHotel] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [bookingLoading, setBookingLoading] = useState(false);
    const [wishlist, setWishlist] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [reviews, setReviews] = useState([]);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { id } = useParams();
    const { slug } = useParams();
    const [showAllReviews, setShowAllReviews] = useState(false);


    useEffect(() => {
        if (!slug) return;

        axios
            .get(`https://dubaicheaphotels.com/api/hotels/${slug}/reviews`)
            .then(res => setReviews(res.data))
            .catch(err => console.error(err));

    }, [slug]);

    useEffect(() => {
        const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        setWishlist(storedWishlist);
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };


    useEffect(() => {
        const fetchRooms = async () => {
            try {
                const res = await axios.get(
                    `https://dubaicheaphotels.com/api/hotels/${slug}/rooms`
                );

                // console.log("ROOMS:", res.data);

                setRooms(res.data);
            } catch (err) {
                console.log(err);
            }
        };

        fetchRooms();
    }, [slug]);




    const toggleWishlist = () => {
        if (!hotel) return;
        const exists = wishlist.find(item => item.id === hotel.id);
        const updated = exists
            ? wishlist.filter(item => item.id !== hotel.id)
            : [...wishlist, hotel];
        updateWishlist(updated);
    };

    const isWishlisted = () => wishlist.some(item => item.id === hotel?.id);

    useEffect(() => {
        const fetchHotel = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`https://dubaicheaphotels.com/api/hotels/${slug}`);
                setHotel(res.data);
            } catch {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        const fetchRooms = async () => {
            try {
                const res = await axios.get(`https://dubaicheaphotels.com/api/hotels/${slug}/rooms`);
                setRooms(res.data);
            } catch { }
        };

        fetchHotel();
        fetchRooms();
    }, [slug]);


    const user = JSON.parse(localStorage.getItem("user"));



    const handleBooking = async () => {
        if (!selectedRoom) return alert("Please select a room first");

        if (!name || !email || !phone || !checkIn || !checkOut) {
            return alert("Please fill all fields");
        }

        if (new Date(checkOut) <= new Date(checkIn)) {
            return alert("Check-out must be after Check-in");
        }

        // ⚠️ reCAPTCHA v3 requires this check
        if (!executeRecaptcha) {
            return alert("Captcha not ready. Please refresh.");
        }

        try {
            setBookingLoading(true);

            // ✅ Generate token dynamically
            const token = await executeRecaptcha("hotel_booking");

            const activeSeason = getBookingSeason(selectedRoom);

            const res = await axios.post("https://dubaicheaphotels.com/api/hotel-payment", {
                user_id: user?.id,
                hotel_id: hotel.id,
                room_id: selectedRoom.id,
                room_name: selectedRoom.name || `Room ${selectedRoom.room_number}`,

                // ✅ PRICE (already correct)
                room_price: getBookingBasePrice(selectedRoom),
                discount_price: selectedRoom.discount_price || null,

                // 🔥 NEW: SEND SEASON DATA
                season_name: activeSeason?.season_name || null,
                season_start: activeSeason?.start_date || null,
                season_end: activeSeason?.end_date || null,

                name,
                email,
                phone,

                check_in: checkIn,
                check_out: checkOut,
                guests,

                total_amount: getBookingTotal(),
                nights: getNights(),

                // ✅ captcha
                captcha: token
            });
            if (res.data.payment_url) {
                window.location.href = res.data.payment_url;
            } else {
                alert("❌ Payment URL not received");
            }

        } catch (err) {
            console.error(err);

            if (err.response) {
                alert(`❌ ${err.response.data.error || err.response.data.message}`);
            } else {
                alert("❌ Server error");
            }
        } finally {
            setBookingLoading(false);
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center">Loading...</div>;
    if (error || !hotel) return <div className="h-screen flex items-center justify-center">Not Found</div>;

    const sliderSettings = {
        dots: false,
        infinite: true,
        speed: 600,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: true,
        pauseOnHover: true
    };

    const amenities = Array.isArray(hotel.amenities)
        ? hotel.amenities
        : hotel.amenities?.split(",") || [];


    const visibleAmenities = showAll ? amenities : amenities.slice(0, 8);
    const remainingCount = amenities.length - 8;


    const amenityIcon = (name) => {
        const value = name.toLowerCase();

        if (value.includes("wifi")) return <FaWifi />;
        if (value.includes("air")) return <FaSnowflake />;
        if (value.includes("breakfast") || value.includes("coffee")) return <FaCoffee />;

        if (value.includes("pool")) return <FaSwimmingPool />;
        if (value.includes("beach")) return <FaUmbrellaBeach />;
        if (value.includes("spa")) return <FaSpa />;
        if (value.includes("gym")) return <FaDumbbell />;
        if (value.includes("parking")) return <FaParking />;
        if (value.includes("shuttle")) return <FaShuttleVan />;
        if (value.includes("restaurant")) return <FaUtensils />;
        if (value.includes("bar")) return <FaCocktail />;
        if (value.includes("room service")) return <FaConciergeBell />;

        return <FaCheck />; // fallback icon
    };


    const getRoomPrice = (room) => {
        return room.discount_price ? Number(room.discount_price) : Number(room.price ?? hotel.price_per_night);
    };

    const getNights = () => {
        if (!checkIn || !checkOut) return 1;

        const start = new Date(checkIn);
        const end = new Date(checkOut);

        const diffTime = end - start;
        const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return nights > 0 ? nights : 1;
    };

    const getBookingTotal = () => {
        if (!selectedRoom) return 0;

        const basePrice = getBookingBasePrice(selectedRoom);

        const service = basePrice * SERVICE_CHARGE;
        const vat = basePrice * VAT;

        // const finalPerNight = Math.round(basePrice + service + vat + TOURISM_FEE);
        const finalPerNight = Math.round(basePrice + service + vat + getTourismFee());

        return finalPerNight * getNights();
    };

    const getOriginalPrice = (room) => {
        return Number(room.price ?? hotel.price_per_night);
    };

    const hasDiscount = (room) => {
        return room.discount_price && Number(room.discount_price) < getOriginalPrice(room);
    };

    const today = new Date().toISOString().split("T")[0];

    const SERVICE_CHARGE = 0.10;
    const VAT = 0.05;
    const TOTAL_TAX = SERVICE_CHARGE + VAT; // 0.15
    // const TOURISM_FEE = 10;

    const getTourismFee = () => {
        return Number(hotel?.rating) === 5 ? 20 : 10;
    };

    const getFinalRoomPrice = (room) => {
        const basePrice = getBookingBasePrice(room);

        const taxPrice = basePrice + (basePrice * TOTAL_TAX);

        // return Math.round(taxPrice + TOURISM_FEE);
        return Math.round(taxPrice + getTourismFee());
    };

    const hasSeasonalPrice = (room) => {
        return room.seasonal_prices && room.seasonal_prices.length > 0;
    };

    const getSeasonalPrice = (date, seasonalPrices, defaultPrice) => {
        const selectedDate = new Date(date);

        const matchedSeason = seasonalPrices.find(season => {
            const start = new Date(season.start_date);
            const end = new Date(season.end_date);

            return selectedDate >= start && selectedDate <= end;
        });

        return matchedSeason ? Number(matchedSeason.price) : defaultPrice;
    };

    const getBookingBasePrice = (room) => {
        if (!room) return 0;

        const selectedDate = checkIn ? new Date(checkIn) : new Date();

        const seasonalPrice = getSeasonalPrice(
            selectedDate,
            room.seasonal_prices || [],
            getOriginalPrice(room)
        );

        // Apply discount if exists
        return room.discount_price
            ? Number(room.discount_price)
            : seasonalPrice;
    };

    // const getBookingSeason = (room) => {
    //     if (!room || !checkIn) return null;

    //     const selectedDate = new Date(checkIn);

    //     return room.seasonal_prices?.find(season => {
    //         const start = new Date(season.start_date);
    //         const end = new Date(season.end_date);
    //         return selectedDate >= start && selectedDate <= end;
    //     });
    // };

    const getBookingSeason = (room) => {
        if (!room) return null;

        // if user selected check-in use it
        // otherwise use today's date
        const selectedDate = checkIn
            ? new Date(checkIn)
            : new Date();

        return room.seasonal_prices?.find(season => {
            const start = new Date(season.start_date);
            const end = new Date(season.end_date);

            return selectedDate >= start && selectedDate <= end;
        });
    };

    return (
        <>
            <Helmet>
                <title>
                    {hotel.meta_title || hotel.name}
                </title>

                <meta
                    name="description"
                    content={hotel.meta_description || hotel.description}
                />

                <link
                    rel="canonical"
                    href={`https://dubaicheaphotels.com/hotels/${hotel.slug}`}
                />

                {/* Open Graph */}
                <meta
                    property="og:title"
                    content={hotel.meta_title || hotel.name}
                />

                <meta
                    property="og:description"
                    content={hotel.meta_description || hotel.description}
                />

                <meta
                    property="og:url"
                    content={`https://dubaicheaphotels.com/hotels/${hotel.slug}`}
                />

                <meta
                    property="og:image"
                    content={
                        hotel?.image
                            ? `https://dubaicheaphotels.com/uploads/hotels/${hotel.image}`
                            : "https://dubaicheaphotels.com/images/default-og.png"
                    }
                />

                <meta property="og:type" content="website" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={hotel.meta_title || hotel.name}
                />
                <meta
                    name="twitter:description"
                    content={hotel.meta_description || hotel.description}
                />
                <meta
                    name="twitter:image"
                    content={
                        hotel?.image
                            ? `https://dubaicheaphotels.com/uploads/hotels/${hotel.image}`
                            : "https://dubaicheaphotels.com/images/default-og.png"
                    }
                />

                {/* SEO */}
                <meta name="robots" content="index, follow" />
            </Helmet>

            <div className="bg-gray-50">

                {/* HERO FULL WIDTH */}
                <div className="relative w-full h-[450px]">
                    <Slider {...sliderSettings} className="h-full">
                        {(hotel.gallery?.length ? hotel.gallery : [hotel.image]).map((img, i) => (
                            <img
                                key={i}
                                src={`https://dubaicheaphotels.com/uploads/hotels/${img}`}
                                className="w-full h-[450px] object-cover"
                                alt=""
                            />
                        ))}
                    </Slider>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

                    <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 text-white max-w-[90%]">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">
                            {hotel.name}
                        </h1>
                        <p className="flex items-center gap-2 mt-2 opacity-90">
                            <FaMapMarkerAlt />

                            <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hotel.location)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline"
                            >
                                {hotel.location}
                            </a>
                        </p>
                    </div>

                    <button
                        onClick={toggleWishlist}
                        className="absolute top-5 right-5 bg-white/90 backdrop-blur p-3 rounded-full shadow hover:scale-110"
                    >
                        {isWishlisted() ? <FaHeart className="text-red-500" /> : <FaRegHeart />}
                    </button>
                </div>

                {/* CONTENT */}
                <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">

                    {/* LEFT */}
                    <div className="md:col-span-2 space-y-6">

                        {/* ABOUT */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm">
                            <h2 className="text-2xl font-bold mb-3">About this hotel</h2>
                            <div
                                className="hotel-content prose max-w-none text-gray-700"
                                dangerouslySetInnerHTML={{ __html: hotel.description }}
                            />

                            {/* AMENITIES */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
                                {visibleAmenities.map((a, i) => (
                                    <div
                                        key={i}
                                        className="group flex items-center gap-3 rounded-xl px-4 py-3 bg-white border border-gray-200 hover:border-teal-300 hover:shadow-md hover:shadow-teal-100 transition-all duration-300"
                                    >
                                        {/* Icon Box */}
                                        <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600 shrink-0 group-hover:bg-orange-100 group-hover:scale-110 transition-all duration-300">
                                            {amenityIcon(a)}
                                        </div>

                                        {/* Text */}
                                        <span className="text-sm font-medium text-gray-800 leading-tight">
                                            {a}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {amenities.length > 8 && (
                                <div className="mt-4">
                                    <button
                                        onClick={() => setShowAll(!showAll)}
                                        className="text-teal-600 font-medium hover:underline transition"
                                    >
                                        {showAll ? "Show less" : `+${remainingCount} more`}
                                    </button>
                                </div>
                            )}

                            {/* CHECK TIMES (HIDDEN IF NULL) */}
                            {(hotel.check_in_time || hotel.check_out_time) && (
                                <div className="flex gap-6 mt-4 text-sm text-gray-500">
                                    {hotel.check_in_time && <p>Check-in: {hotel.check_in_time}</p>}
                                    {hotel.check_out_time && <p>Check-out: {hotel.check_out_time}</p>}
                                </div>
                            )}
                        </div>

                        {/* ROOMS */}
                        <div className="space-y-6">
                            {rooms.map(room => {
                                let imgs = [];
                                try { imgs = room.images ? JSON.parse(room.images) : []; } catch { }

                                // ✅ ONE selectedDate only
                                const selectedDate = checkIn ? new Date(checkIn) : new Date();

                                // ✅ Match season (for badge)
                                const matchedSeason = room.seasonal_prices?.find(season => {
                                    const start = new Date(season.start_date);
                                    const end = new Date(season.end_date);
                                    return selectedDate >= start && selectedDate <= end;
                                });

                                const isSeasonal = !!matchedSeason;
                                const isDiscounted = hasDiscount(room);

                                // ✅ Get seasonal price (your logic)
                                const seasonalBasePrice = getSeasonalPrice(
                                    selectedDate,
                                    room.seasonal_prices || [],
                                    getOriginalPrice(room)
                                );

                                // ✅ Keep your pricing logic EXACT
                                const original = seasonalBasePrice;

                                const price = room.discount_price
                                    ? Number(room.discount_price)
                                    : seasonalBasePrice;

                                const saved =
                                    isDiscounted ? Number(original) - Number(price) : 0;

                                const discountPercent =
                                    isDiscounted && original
                                        ? Math.round(((original - price) / original) * 100)
                                        : 0;

                                return (
                                    <div
                                        key={room.id}
                                        onClick={() => setSelectedRoom(room)}
                                        className={`bg-white rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer
            hover:shadow-xl hover:-translate-y-[2px]
            ${selectedRoom?.id === room.id ? "border-teal-500 ring-2 ring-teal-200" : ""}
            ${isSeasonal ? "border-orange-300 bg-orange-50/30" : ""}
            ${isDiscounted ? "border-green-300 bg-green-50/30" : ""}
            `}
                                    >
                                        <div className="flex flex-col lg:flex-row">

                                            {/* IMAGE */}
                                            <div className="relative w-full lg:w-1/3 h-48">

                                                {/* 🔥 SEASONAL BADGE */}
                                                {isSeasonal && (
                                                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">

                                                        <span className="bg-orange-100 text-orange-700 text-[11px] font-semibold px-3 py-1 rounded-full shadow-sm border border-orange-200 flex items-center gap-1">
                                                            <FaFire className="text-orange-500 text-[10px]" />
                                                            Limited Time
                                                        </span>

                                                        <span className="bg-white/90 backdrop-blur text-orange-600 text-[10px] px-2 py-[2px] rounded-full shadow-sm border border-orange-100 flex items-center gap-1">
                                                            <FaCalendarAlt className="text-orange-500 text-[10px]" />
                                                            Seasonal Offer
                                                        </span>

                                                    </div>
                                                )}

                                                {/*  DISCOUNT BADGE */}
                                                {isDiscounted && (
                                                    <div className="absolute top-3 right-3 z-10">
                                                        <span className="bg-green-100 text-green-700 text-[11px] font-semibold px-3 py-1 rounded-full shadow-sm border border-green-200 flex items-center gap-1">
                                                            <FaTag className="text-green-600 text-[10px]" />
                                                            {discountPercent}% OFF
                                                        </span>
                                                    </div>
                                                )}

                                                <Slider {...sliderSettings}>
                                                    {(imgs.length ? imgs : [room.image]).map((img, i) => (
                                                        <img
                                                            key={i}
                                                            src={`https://dubaicheaphotels.com/uploads/rooms/${img}`}
                                                            className="h-48 w-full object-cover transition-transform duration-500 hover:scale-105"
                                                            alt=""
                                                        />
                                                    ))}
                                                </Slider>
                                            </div>

                                            {/* CONTENT */}
                                            <div className="p-5 flex-1">
                                                <div className="flex justify-between items-start gap-4">

                                                    {/* TITLE */}
                                                    <div>
                                                        <h4 className="font-semibold text-lg text-gray-900">
                                                            {`${room.room_number}` || room.name}
                                                        </h4>

                                                        <p className="text-sm text-gray-500 mt-1">
                                                            👤 {room.capacity} guests • 🛏 {room.beds_count ? `${room.beds_count} beds` : '1 bed'}
                                                        </p>
                                                    </div>

                                                    {/* PRICE */}
                                                    <div className="text-right">
                                                        <p className="text-teal-600 font-bold text-lg">
                                                            {price.toLocaleString()} AED
                                                        </p>

                                                        {isDiscounted && (
                                                            <>
                                                                <p className="text-xs text-gray-400 line-through">
                                                                    {original.toLocaleString()} AED
                                                                </p>

                                                                {/*  SAVING MESSAGE */}
                                                                <p className="text-[11px] text-green-600 font-medium mt-1">
                                                                    Save {saved.toLocaleString()} AED
                                                                </p>
                                                            </>
                                                        )}

                                                        {/* 🔥 Seasonal Info */}
                                                        {isSeasonal && (
                                                            <p className="text-[11px] text-orange-600 font-medium mt-1">
                                                                ⏳ Seasonal price (may change soon)
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* FEATURES */}
                                                <div className="flex gap-2 mt-3 flex-wrap text-xs">
                                                    <span className="bg-gray-100 px-2 py-1 rounded">WiFi</span>
                                                    <span className="bg-gray-100 px-2 py-1 rounded">AC</span>
                                                    <span className="bg-gray-100 px-2 py-1 rounded">Breakfast</span>
                                                </div>

                                                {/* CTA */}
                                                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/hotels/${hotel.slug}/${room.id}`);
                                                        }}
                                                        className="flex-1 border rounded-lg py-2 text-sm hover:bg-gray-100"
                                                    >
                                                        Details
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedRoom(room);
                                                        }}
                                                        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all
                                ${selectedRoom?.id === room.id
                                                                ? "bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-md"
                                                                : "bg-teal-100 hover:bg-teal-200"
                                                            }`}
                                                    >
                                                        {selectedRoom?.id === room.id ? "Selected ✓" : "Select Room"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* BOOKING CARD */}
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-xl lg:sticky lg:top-24">

                        {/* HEADER */}
                        <div className="p-5 border-b bg-gradient-to-r from-teal-500 to-emerald-500 text-white">
                            <h3 className="text-lg font-semibold">Complete Your Booking</h3>
                            <p className="text-xs opacity-90 mt-1">
                                Secure reservation • Instant confirmation
                            </p>
                        </div>

                        {/* FORM */}
                        <div className="p-5 space-y-4">

                            {/* Name */}
                            <div>
                                <label className="text-xs font-medium text-gray-600">Full Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    className="w-full mt-1 border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-teal-400 outline-none text-sm"
                                    placeholder="John Doe"
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label className="text-xs font-medium text-gray-600">Email Address</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="w-full mt-1 border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-teal-400 outline-none text-sm"
                                    placeholder="example@email.com"
                                />
                            </div>

                            {/* Phone */}
                            <div>
                                <label className="text-xs font-medium text-gray-600">Phone Number</label>
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                    className="w-full mt-1 border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-teal-400 outline-none text-sm"
                                    placeholder="+971 50 XXX XXXX"
                                />
                            </div>

                            {/* Dates */}
                            {/* <div className="flex flex-col sm:flex-row gap-3">

                                <div>
                                    <label className="text-xs font-medium text-gray-600">Check-in</label>
                                    <input
                                        type="date"
                                        value={checkIn}
                                        min={today}
                                        onChange={e => {
                                            setCheckIn(e.target.value);
                                            if (checkOut && e.target.value > checkOut) setCheckOut("");
                                        }}
                                        className="w-full min-w-0 max-w-full block mt-1 border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-teal-400 outline-none text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-gray-600">Check-out</label>
                                    <input
                                        type="date"
                                        value={checkOut}
                                        min={checkIn || today}
                                        onChange={e => setCheckOut(e.target.value)}
                                        className="w-full min-w-0 max-w-full block mt-1 border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-teal-400 outline-none text-sm"
                                    />
                                </div>
                            </div> */}


                            {/* Dates */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full min-w-0 overflow-hidden">

                                <div className="min-w-0 w-full">
                                    <label className="text-xs font-medium text-gray-600">Check-in</label>
                                    <input
                                        type="date"
                                        value={checkIn}
                                        min={today}
                                        onChange={e => {
                                            setCheckIn(e.target.value);
                                            if (checkOut && e.target.value > checkOut) setCheckOut("");
                                        }}
                                        className="w-full min-w-0 max-w-full block mt-1 border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-teal-400 outline-none text-sm"
                                    />
                                </div>

                                <div className="min-w-0 w-full">
                                    <label className="text-xs font-medium text-gray-600">Check-out</label>
                                    <input
                                        type="date"
                                        value={checkOut}
                                        min={checkIn || today}
                                        onChange={e => setCheckOut(e.target.value)}
                                        className="w-full min-w-0 max-w-full block mt-1 border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-teal-400 outline-none text-sm"
                                    />
                                </div>

                            </div>

                            {/* Guests */}
                            <div>
                                <label className="text-xs font-medium text-gray-600">Guests</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={guests}
                                    onChange={e => setGuests(e.target.value)}
                                    className="w-full mt-1 border border-gray-200 p-3 rounded-lg focus:ring-2 focus:ring-teal-400 outline-none text-sm"
                                />
                            </div>
                        </div>

                        {/* PRICE SUMMARY */}
                        {/* PRICE SUMMARY */}
                        {selectedRoom && (
                            <div className="bg-gray-50 border-t p-5 space-y-3">

                                {(() => {
                                    const activeSeason = getBookingSeason(selectedRoom);

                                    return (
                                        <>
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="text-sm text-gray-500">Selected Room</p>
                                                    <p className="font-semibold text-gray-900">
                                                        {selectedRoom.name || `Room ${selectedRoom.room_number}`}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500">Base Price / night</span>
                                                <span className="font-medium">
                                                    {getBookingBasePrice(selectedRoom).toLocaleString()} AED
                                                </span>
                                            </div>

                                            {/* 🔥 SEASONAL MESSAGE */}
                                            {activeSeason && (
                                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                                                    <p className="text-xs font-semibold text-orange-700">
                                                        🔥 Seasonal Price Applied
                                                    </p>

                                                    <p className="text-xs text-orange-600 mt-1">
                                                        {activeSeason.season_name} season
                                                        ({activeSeason.start_date} → {activeSeason.end_date})
                                                    </p>

                                                    <p className="text-[11px] text-orange-500 mt-1">
                                                        Price depends on selected dates
                                                    </p>
                                                </div>
                                            )}

                                            {!activeSeason && (
                                                <p className="text-xs text-gray-500">
                                                    Standard room pricing applied
                                                </p>
                                            )}

                                            {/* <div className="text-xs text-gray-500">
                                                Includes 10% service charge + 5% VAT + AED 10 tourism fee
                                            </div> */}

                                            <div className="text-xs text-gray-500">
                                                Includes:
                                                {" "}10% service charge,
                                                {" "}5% VAT,
                                                {" "}AED {getTourismFee()} tourism fee
                                                {Number(hotel?.rating) === 5 && " (5-Star Hotel Fee)"}
                                            </div>

                                            <div className="border-t pt-3 flex justify-between items-center">
                                                <span className="text-gray-700 font-medium">Final / night</span>
                                                <span className="text-lg font-bold text-teal-600">
                                                    {getFinalRoomPrice(selectedRoom).toLocaleString()} AED
                                                </span>
                                            </div>

                                            {checkIn && checkOut && (
                                                <div className="pt-2 space-y-1 text-sm">

                                                    <div className="flex justify-between text-gray-600">
                                                        <span>Nights</span>
                                                        <span className="font-medium">{getNights()}</span>
                                                    </div>

                                                    <div className="flex justify-between text-gray-900 font-bold text-base">
                                                        <span>Total</span>
                                                        <span>
                                                            {getBookingTotal().toLocaleString()} AED
                                                        </span>
                                                    </div>

                                                    <p className="text-xs text-gray-500">
                                                        All taxes & fees included
                                                    </p>
                                                </div>
                                            )}

                                            {/* <p className="text-xs text-orange-600 font-medium">
                                                ⚠ AED 10 tourism fee per night applied as per UAE regulations
                                            </p> */}

                                            <p className="text-xs text-orange-600 font-medium">
                                                ⚠ AED {getTourismFee()} tourism fee per night applied as per UAE regulations
                                            </p>
                                        </>
                                    );
                                })()}
                            </div>
                        )}

                        {/* BUTTON */}

                        <div className="p-5">
                            <button
                                onClick={handleBooking}
                                disabled={!selectedRoom || bookingLoading}
                                className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {bookingLoading ? (
                                    <>
                                        {/* Spinner */}
                                        <svg
                                            className="animate-spin h-5 w-5 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>

                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 
                        5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 
                        5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>

                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    "Confirm & Book Now"
                                )}
                            </button>

                            <p className="text-xs text-center text-gray-500 mt-2">
                                You will be redirected to secure payment
                            </p>
                        </div>


                    </div>
                </div>

                <section className="max-w-7xl mx-auto px-4 pb-20">
                    <div className="mt-16">

                        {/* HEADER */}
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                            <div>
                                <p className="text-xs uppercase tracking-[0.25em] text-teal-600 font-semibold mb-2">
                                    Guest Experience
                                </p>

                                <h2 className="text-3xl font-bold text-gray-900">
                                    Guest Reviews
                                </h2>

                                <p className="text-gray-500 text-sm mt-2">
                                    Real feedback from verified guests
                                </p>
                            </div>

                            {reviews.length > 0 && (
                                <div className="bg-white border rounded-2xl px-6 py-4 shadow-sm text-center">
                                    <p className="text-xs text-gray-400 uppercase mb-1">
                                        Total Reviews
                                    </p>
                                    <p className="text-2xl font-bold text-gray-800">
                                        {reviews.length}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* EMPTY STATE */}
                        {reviews.length === 0 ? (
                            <div className="bg-gradient-to-br from-gray-50 to-white border rounded-3xl py-24 text-center shadow-sm">
                                <div className="flex justify-center mb-6">
                                    <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-yellow-50 border border-yellow-100 shadow-sm">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7 text-yellow-500"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 7.1-1.01z" />
                                        </svg>
                                    </div>
                                </div>

                                <h3 className="text-xl font-semibold text-gray-800">
                                    No reviews yet
                                </h3>

                                <p className="text-gray-500 text-sm mt-2">
                                    Be the first to share your experience
                                </p>
                            </div>
                        ) : (

                            /* REVIEWS LIST */
                            <div className="space-y-6">
                                {(showAllReviews ? reviews : reviews.slice(0, 4)).map((r) => {

                                    const displayName = r.email
                                        ?.split("@")[0]
                                        ?.replace(/\./g, " ")
                                        ?.replace(/\b\w/g, l => l.toUpperCase());

                                    return (
                                        <div
                                            key={r.id}
                                            className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-lg transition-all duration-300"
                                        >
                                            {/* TOP */}
                                            <div className="flex flex-col md:flex-row md:justify-between gap-5 mb-5">

                                                {/* USER */}
                                                <div className="flex items-center gap-4">
                                                    <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white flex items-center justify-center font-bold text-lg shadow">
                                                        {r.email.charAt(0).toUpperCase()}
                                                    </div>

                                                    <div>
                                                        <h4 className="font-semibold text-gray-900">
                                                            {displayName}
                                                        </h4>

                                                        <div className="flex flex-wrap gap-2 mt-1">
                                                            <span className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium">
                                                                Verified Guest
                                                            </span>

                                                            <span className="text-xs text-gray-400">
                                                                {dayjs(r.created_at).fromNow()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* RATING */}
                                                <div className="md:text-right">
                                                    <div className="inline-flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-2xl px-4 py-3">
                                                        <span className="text-xl font-bold text-yellow-700">
                                                            {r.rating}.0
                                                        </span>

                                                        <div>
                                                            <div className="flex gap-1 justify-start md:justify-end">
                                                                {[1, 2, 3, 4, 5].map((star) => (
                                                                    <span
                                                                        key={star}
                                                                        className={`text-lg ${star <= r.rating
                                                                            ? "text-yellow-400"
                                                                            : "text-gray-300"
                                                                            }`}
                                                                    >
                                                                        ★
                                                                    </span>
                                                                ))}
                                                            </div>

                                                            <p className="text-[11px] text-gray-500 mt-1">
                                                                Guest Rating
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* DIVIDER */}
                                            <div className="border-t border-gray-100 mb-5"></div>

                                            {/* COMMENT */}
                                            <p className="text-gray-700 leading-7 text-[15px]">
                                                “{r.comment}”
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {reviews.length > 4 && (
                            <div className="flex justify-center mt-8">
                                <button
                                    onClick={() => setShowAllReviews(!showAllReviews)}
                                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300"
                                >
                                    {showAllReviews
                                        ? "Show Less Reviews"
                                        : `View All ${reviews.length} Reviews`}
                                </button>
                            </div>
                        )}
                    </div>
                </section>



            </div>
        </>
    );
}